import React, { createContext, useContext, useState, useEffect } from 'react';
import { saveLocationToLocalStorage, getUserLocation } from '../utils/locationService';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { useUser } from './UserContext';

// Create the location context
const LocationContext = createContext({
  location: null,
  isLoading: false,
  error: null,
  setLocation: () => {},
  updateLocation: () => {},
  getCurrentLocation: () => {},
  clearLocation: () => {}
});

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

// Location provider component
export const LocationProvider = ({ children }) => {
  const [location, setLocationState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useUser();

  // Constants
  const LOCATION_STORAGE_KEY = 'petzify_user_location';

  // Initialize location from storage on mount
  useEffect(() => {
    initializeLocation();
  }, []);

  // Watch for user changes and sync location
  useEffect(() => {
    if (currentUser) {
      // When user logs in, sync with their database location
      syncWithDatabase();
    }
  }, [currentUser]);

  const initializeLocation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try to get location from localStorage first
      const storedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (storedLocation) {
        const parsedLocation = JSON.parse(storedLocation);
        setLocationState(parsedLocation);
        setIsLoading(false);
        return;
      }

      // No stored location. Never trigger the browser permission prompt on
      // page load — only auto-detect silently when the user has already
      // granted geolocation access; otherwise wait for a user gesture
      // (the "Select location" control opens the LocationDrawer).
      if (navigator.permissions && navigator.permissions.query) {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state === 'granted') {
          await getCurrentLocation();
        }
      }
    } catch (err) {
      console.error('Error initializing location:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const syncWithDatabase = async () => {
    if (!currentUser?.email) return;

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', currentUser.email);
      
      // If we have a current location, save it to database
      if (location) {
        await updateDoc(userRef, {
          location: {
            latitude: location.latitude,
            longitude: location.longitude,
            displayName: location.cityName || location.locationName,
            lastUpdated: new Date().toISOString()
          }
        });
      }
    } catch (err) {
      console.error('Error syncing location with database:', err);
    }
  };

  const getCurrentLocation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await getUserLocation({
        useLocalStorage: false, // Force fresh location
        onLocationUpdate: (locationData) => {
          if (locationData) {
            const normalizedLocation = normalizeLocationData(locationData);
            setLocationState(normalizedLocation);
            saveToStorage(normalizedLocation);
            
            // Sync with database if user is logged in
            if (currentUser?.email) {
              syncWithDatabase();
            }
          }
        },
        onError: (err) => {
          setError(err.message);
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateLocation = (locationData) => {
    const normalizedLocation = normalizeLocationData(locationData);
    setLocationState(normalizedLocation);
    saveToStorage(normalizedLocation);
    
    // Sync with database if user is logged in
    if (currentUser?.email) {
      syncWithDatabase();
    }
    
    // Clear any previous errors
    setError(null);
  };

  const clearLocation = () => {
    setLocationState(null);
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    setError(null);
  };

  const normalizeLocationData = (locationData) => {
    return {
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      cityName: locationData.cityName || extractCityFromAddress(locationData.locationName || locationData.address),
      locationName: locationData.locationName || locationData.address,
      address: locationData.address || locationData.locationName,
      city: locationData.city || '',
      state: locationData.state || '',
      country: locationData.country || '',
      pincode: locationData.pincode || '',
      placeId: locationData.placeId || null,
      lastUpdated: locationData.lastUpdated || new Date().toISOString()
    };
  };

  const extractCityFromAddress = (address) => {
    if (!address) return '';
    const parts = address.split(',');
    return parts[0].trim();
  };

  const saveToStorage = (locationData) => {
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locationData));
      
      // Also clean up old storage keys for consistency
      localStorage.removeItem('preferredLocation');
      localStorage.removeItem('userLocation');
    } catch (err) {
      console.error('Error saving location to storage:', err);
    }
  };

  const contextValue = {
    location,
    isLoading,
    error,
    setLocation: updateLocation,
    updateLocation,
    getCurrentLocation,
    clearLocation
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext; 